import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Typography from '@material-ui/core/Typography';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Alert from '@material-ui/lab/Alert';
import MuiDialog from 'Components/UI/Dialog';
import Button from 'Components/UI/Button';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { CircularProgress } from '@material-ui/core';

const styles = theme => ({
  root: {},
  title: {
    paddingRight: theme.spacing(6),
    color: theme.palette.primary.main,
  },
  alertMessage: {
    marginBottom: theme.spacing(4),
    fontSize: '1em',
  },
});

const Dialog = ({
  classes,
  onConfirm,
  onCancel,
  onClose,
  message,
  alertMessage,
  alertType,
  children,
  title,
  cancelLabel,
  confirmLabel,
  className,
  loading,
  ConfirmButtonProps,
  CancelButtonProps,
  DialogActionsProps,
  loadingCancelButton,
  makeCancelDefaultAction,
  ...rest
}) => (
  <MuiDialog
    className={classNames(classes.root, className)}
    maxWidth="xs"
    onClose={onClose}
    open
    {...rest}
  >
    <MuiDialogTitle
      className={classes.title}
    >
      {title}
    </MuiDialogTitle>
    <MuiDialogContent>
      {alertMessage && (
      <Alert
        className={classes.alertMessage}
        severity={alertType}
      >
        {alertMessage}
      </Alert>
      )}
      <Typography variant="body1">{message}</Typography>
      {children}
    </MuiDialogContent>
    <MuiDialogActions {...DialogActionsProps}>
      <Button
        color="primary"
        variant={makeCancelDefaultAction ? 'contained' : 'text'}
        onClick={onCancel}
        test={'confirmation-dialog-cancel'}
        round
        {...CancelButtonProps}
      >
        {loadingCancelButton ? <CircularProgress size={24} /> : cancelLabel}
      </Button>
      <Button
        color="primary"
        disableElevation
        round
        variant={makeCancelDefaultAction ? 'text' : 'contained'}
        onClick={onConfirm}
        disabled={loading}
        test={'confirmation-dialog-confirm'}
        {...ConfirmButtonProps}
      >
        {loading && <CircularProgress size={24} />}
        {!loading && confirmLabel}
      </Button>
    </MuiDialogActions>
  </MuiDialog>
);

Dialog.propTypes = {
  children: PropTypes.node,
  message: PropTypes.string,
  alertMessage: PropTypes.string,
  alertType: PropTypes.oneOf(['info', 'error', 'warning', 'success']),
  title: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  cancelLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  loadingCancelButton: PropTypes.bool,
  ConfirmButtonProps: PropTypes.object,
  CancelButtonProps: PropTypes.object,
  DialogActionsProps: PropTypes.object,
  makeCancelDefaultAction: PropTypes.bool,
};

Dialog.defaultProps = {
  cancelLabel: 'Cancel',
  message: '',
  children: null,
  confirmLabel: 'Yes',
  title: 'Are you sure?',
  className: '',
  alertMessage: null,
  alertType: 'info',
  loading: false,
  loadingCancelButton: false,
  ConfirmButtonProps: {},
  CancelButtonProps: {},
  DialogActionsProps: {},
  makeCancelDefaultAction: false,
};

export default withStyles(styles)(Dialog);
